/**
 * A paleta da cartela e as contas de contraste.
 *
 * Compartilhado porque as MESMAS cores aparecem no painel (o seletor da
 * lojista), no app da cliente (carimbo, avatar, botões) e no servidor, que
 * valida a escolha. Três listas iguais divergiriam no primeiro tom novo — e a
 * lojista escolheria uma cor que o servidor recusa.
 */

/**
 * As cinco que ficam à mão, sem abrir nada.
 *
 * ⚠️ A primeira é o laranja da Cerchia: a loja que não pensa no assunto cai na
 * cor do produto, que é a que já combina com tudo. As outras quatro são os
 * ramos mais comuns — barbearia, salão, clínica, pet.
 */
export const CORES_RAPIDAS = [
  '#d4622a', // o laranja da Cerchia
  '#a8574a', // terracota
  '#6f8f74', // sálvia
  '#6b84a8', // azul poeira
  '#8a6f5c', // café com leite
];

/**
 * O resto da paleta, atrás do `+`.
 *
 * ⚠️ **Tons médios, não pastéis claríssimos.** Todo carimbo é um bloco de cor
 * sólido de 48px, e a inicial da loja é texto sobre a cor — abaixo de certa
 * luminosidade o desenho vira mancha e a letra some. Estes tons são
 * dessaturados o bastante para conversar com o bege do sistema, e escuros o
 * bastante para o desenho continuar existindo.
 */
export const CORES_EXTRAS = [
  '#c07855', // caramelo
  '#b8734f', // âmbar queimado
  '#a35f63', // rosa queimado
  '#95566b', // amora
  '#7d6a94', // lavanda escura
  '#5f6f9c', // azul lavanda
  '#4a7883', // petróleo
  '#5c8a76', // eucalipto
  '#7d8f5c', // oliva
  '#a08a4a', // mostarda seca
  '#8f7a5c', // areia torrada
  '#7a7268', // cinza quente
];

export const CORES = [...CORES_RAPIDAS, ...CORES_EXTRAS];

/** A cor está na paleta? O servidor pergunta isso antes de gravar. */
export const corValida = (cor: string): boolean => CORES.includes(cor);

/**
 * Luminância relativa (WCAG). Serve para decidir o que se escreve por cima.
 *
 * ⚠️ Não é a média dos canais: o olho enxerga verde muito mais que azul, e uma
 * média simples faria o azul-escuro parecer claro. A fórmula com os pesos
 * .2126/.7152/.0722 é a do padrão, e é ela que impede a letra branca sumir
 * num tom que "parecia escuro".
 */
export function luminancia(hex: string): number {
  const n = hex.replace('#', '');
  const canais = [0, 2, 4].map((i) => {
    const v = parseInt(n.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * canais[0]! + 0.7152 * canais[1]! + 0.0722 * canais[2]!;
}

/** Razão de contraste entre duas luminâncias (WCAG). 1:1 = idênticas. */
export function contraste(a: string, b: string): number {
  const [hi, lo] = [luminancia(a), luminancia(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** A tinta escura da casa — a mesma do `--tinta` do painel. */
const ESCURO = '#14120f';
const CLARO = '#ffffff';

/**
 * O que escrever EM CIMA desta cor: branco ou a tinta escura.
 *
 * ⚠️ **Compara o contraste das duas, não chuta por um corte de luminância.**
 * A primeira versão usava "claro se a luminância passar de 0.45", e o teste
 * mostrou o erro: o laranja da Cerchia ficava com texto branco a 3,8:1 —
 * abaixo do mínimo de 4,5:1 do WCAG — quando com a tinta escura chegava a
 * 5,2:1. Um corte fixo erra justamente nos tons médios, que são a paleta
 * inteira.
 */
export const tintaSobre = (cor: string): string =>
  contraste(cor, CLARO) >= contraste(cor, ESCURO) ? CLARO : ESCURO;

/**
 * O anel de dentro do carimbo, que é o que faz parecer tinta de borracha.
 *
 * Branco translúcido some em cor clara; preto translúcido some em cor escura.
 * Acompanha a mesma decisão do texto, pela mesma conta.
 */
export const mioloSobre = (cor: string): string =>
  tintaSobre(cor) === CLARO ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.22)';

/**
 * O contraste que a inicial da loja alcança nesta cor.
 *
 * Existe para o teste conferir a paleta inteira de uma vez: cor que não chega
 * a 4,5:1 com NENHUMA das duas tintas não deveria estar na lista, e é melhor
 * descobrir isso aqui do que na tela de uma cliente.
 */
export const contrasteDaTinta = (cor: string): number =>
  Math.max(contraste(cor, CLARO), contraste(cor, ESCURO));

// ─── oklab (o tema do quiz) ─────────────────────────────────────────────

type Oklab = [number, number, number];

const paraLinear = (v: number): number =>
  v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
const deLinear = (v: number): number =>
  v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;

function hexParaOklab(hex: string): Oklab {
  const n = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => paraLinear(parseInt(n.slice(i, i + 2), 16) / 255)) as [
    number,
    number,
    number,
  ];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function oklabParaHex([L, a, b]: Oklab): string {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const canais = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
  return `#${canais
    .map((c) => {
      const v = Math.round(deLinear(Math.min(1, Math.max(0, c))) * 255);
      return v.toString(16).padStart(2, '0');
    })
    .join('')}`;
}

/**
 * Mistura em oklab, como o `color-mix(in oklab, a p%, b)` do CSS. `p` vai de 0 a 1 (a parte
 * de `a`). Devolve #rrggbb.
 */
export function misturarOklab(a: string, p: number, b: string): string {
  const x = hexParaOklab(a);
  const y = hexParaOklab(b);
  return oklabParaHex([
    x[0] * p + y[0] * (1 - p),
    x[1] * p + y[1] * (1 - p),
    x[2] * p + y[2] * (1 - p),
  ]);
}

/** A faixa de luminosidade e o croma máximo em que o tema do quiz tem contraste garantido. */
export const FAIXA_DO_TEMA = { lMin: 0.45, lMax: 0.62, cromaMax: 0.14 } as const;

/**
 * A cor da loja dentro da faixa em que o tema do quiz tem contraste garantido: L em
 * [0,45; 0,62] e croma ≤ 0,14 (em oklab). Na cartela a mudança é imperceptível; nos
 * extremos (#000, #fff, amarelo puro) é ela que mantém o texto legível.
 */
export function corDoTema(hex: string): string {
  const [L, a, b] = hexParaOklab(hex);
  const croma = Math.hypot(a, b);
  const f = croma > FAIXA_DO_TEMA.cromaMax ? FAIXA_DO_TEMA.cromaMax / croma : 1;
  const Lc = Math.min(FAIXA_DO_TEMA.lMax, Math.max(FAIXA_DO_TEMA.lMin, L));
  return oklabParaHex([Lc, a * f, b * f]);
}
